import { gql, type ApolloClient } from "@apollo/client";
import type {
  CloudConfiguration,
  MarlinStore,
} from "@observe-tech/marlin-store/core";
import type {
  CloudConfigurationResult,
  ShellHandle,
} from "@observe-tech/mfe-shell-api";

type CloudRow =
  | { id: string; configurationData?: CloudConfiguration }
  | null
  | undefined;

const GET_USER_APP_CONFIGURATION = gql`
  query GetUserAppConfiguration {
    userAppConfiguration {
      id
      userEmail
      configurationData
      createdAt
      updatedAt
    }
  }
`;

const SAVE_USER_APP_CONFIGURATION = gql`
  mutation SaveUserAppConfiguration($input: CreateUserAppConfigurationDto!) {
    saveUserAppConfiguration(input: $input) {
      id
      userEmail
      configurationData
      createdAt
      updatedAt
    }
  }
`;

const failure = (error: unknown): CloudConfigurationResult => ({
  success: false,
  error: error instanceof Error ? error.message : String(error),
});

export const createCloudConfigurationAction = (
  cloudClient: ApolloClient<unknown>,
  store: MarlinStore,
  getToken: () => string | null | Promise<string | null>,
): ShellHandle["cloudConfiguration"] => {
  const requireSession = async (): Promise<void> => {
    if (!(await getToken()))
      throw new Error("The shell session is not available");
  };

  return {
    download: async () => {
      try {
        await requireSession();
        const { data } = await cloudClient.query<{
          userAppConfiguration: CloudRow;
        }>({ query: GET_USER_APP_CONFIGURATION, fetchPolicy: "no-cache" });
        const config = data?.userAppConfiguration?.configurationData;
        if (!config)
          return { success: false, error: "No configuration found in cloud" };
        await store.cloud.importConfiguration(config);
        return { success: true };
      } catch (error) {
        return failure(error);
      }
    },
    upload: async () => {
      try {
        await requireSession();
        const configurationData = await store.cloud.exportConfiguration();
        const { data } = await cloudClient.mutate<{
          saveUserAppConfiguration: CloudRow;
        }>({
          mutation: SAVE_USER_APP_CONFIGURATION,
          variables: { input: { configurationData } },
        });
        if (!data?.saveUserAppConfiguration)
          throw new Error("No data returned from cloud");
        await store.cloud.markSynced(configurationData.syncedAt);
        return { success: true };
      } catch (error) {
        return failure(error);
      }
    },
  };
};
